import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { collectClientContext } from '../api/clientContext';
import { fetchConsent, submitConsent } from '../api/consentClient';
import type { PartnerConsentPreload } from '../api/types';
import { AppShell } from '../components/AppShell';
import { AlreadyConsentedState } from './states/AlreadyConsentedState';
import { ErrorState } from './states/ErrorState';
import { ExpiredState } from './states/ExpiredState';
import { InvalidState } from './states/InvalidState';
import { LoadingState } from './states/LoadingState';
import { PreloadState } from './states/PreloadState';
import { SuccessState } from './states/SuccessState';

type PageState =
  | { kind: 'loading' }
  | { kind: 'preload'; data: PartnerConsentPreload; submitting: boolean }
  | { kind: 'success' }
  | { kind: 'expired' }
  | { kind: 'already-consented' }
  | { kind: 'invalid' }
  | { kind: 'error' };

const SESSION_KEY = 'credisa.consent.sessionId';

function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return crypto.randomUUID();
  }
}

export function ConsentPage() {
  const { token } = useParams<{ token: string }>();
  const { i18n } = useTranslation();
  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const sessionIdRef = useRef<string>(getOrCreateSessionId());

  const load = useCallback(async () => {
    if (!token) {
      setState({ kind: 'invalid' });
      return;
    }
    setState({ kind: 'loading' });
    const result = await fetchConsent(token);
    if (result.kind === 'preload') {
      setState({ kind: 'preload', data: result.data, submitting: false });
    } else if (result.kind === 'gone') {
      setState({ kind: mapGoneReason(result.reason) });
    } else {
      setState({ kind: 'error' });
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = useCallback(async () => {
    if (!token) return;
    setState((prev) => (prev.kind === 'preload' ? { ...prev, submitting: true } : prev));

    const body = await collectClientContext({
      sessionId: sessionIdRef.current,
      displayLanguage: i18n.resolvedLanguage ?? null,
    });
    const result = await submitConsent(token, body);

    if (result.kind === 'success') {
      setState({ kind: 'success' });
    } else if (result.kind === 'gone') {
      setState({ kind: mapGoneReason(result.reason) });
    } else {
      setState((prev) =>
        prev.kind === 'preload' ? { ...prev, submitting: false } : { kind: 'error' },
      );
    }
  }, [token, i18n.resolvedLanguage]);

  const body = useMemo(() => {
    switch (state.kind) {
      case 'loading':
        return <LoadingState />;
      case 'preload':
        return (
          <PreloadState data={state.data} submitting={state.submitting} onSubmit={handleSubmit} />
        );
      case 'success':
        return <SuccessState />;
      case 'expired':
        return <ExpiredState />;
      case 'already-consented':
        return <AlreadyConsentedState />;
      case 'invalid':
        return <InvalidState />;
      case 'error':
        return <ErrorState onRetry={() => void load()} />;
    }
  }, [state, handleSubmit, load]);

  return <AppShell>{body}</AppShell>;
}

function mapGoneReason(
  reason: 'Expired' | 'AlreadyConsented' | 'Invalid',
): 'expired' | 'already-consented' | 'invalid' {
  switch (reason) {
    case 'Expired':
      return 'expired';
    case 'AlreadyConsented':
      return 'already-consented';
    case 'Invalid':
    default:
      return 'invalid';
  }
}
